import { ReactNode, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import {
    sandboxCreate,
    sandboxDelete,
    sandboxExpireGrace,
    sandboxFlags,
    sandboxInjectUsage,
    sandboxList,
    sandboxRefund,
    sandboxReset,
    sandboxRevokeMandate,
    sandboxRunJob,
    sandboxState,
    sandboxWebhook,
} from "@/api";
import { appState } from "@/app/appState";
import { EmptyRow, Pill, PillTone, Row, Section } from "@/app/workspace/settings/ui";

/**
 * Billing Lab: super admins run the whole payment flow on a billing sandbox,
 * an organisation whose billing uses the Mollie test API. The customer side
 * (add card, budget, invoices) is the normal billing settings of the sandbox;
 * this page is the operator side: fake usage, the 1st-of-month charge on a
 * button, Mollie's answers, and a checklist of scenarios that checks itself
 * against the sandbox's state.
 */

type Sandbox = {
    id: number;
    name: string;
    parent_organisation: number | null;
    billing_status: string;
    has_card: boolean;
    join_next_cron: boolean;
    created_at: string;
};

type Period = {
    id: number;
    period_start: string;
    period_end: string;
    cost_eur: number;
    net_amount: number;
    status: string;
    carried_into_id: number | null;
    charge_net: number | null;
    vat_amount: number | null;
    total: number | null;
    mollie_payment_id: string | null;
    dst_change: boolean;
};

type MolliePayment = {
    status: string | null;
    amount: string | null;
    amount_refunded: string | null;
    amount_charged_back: string | null;
    sequence_type: string | null;
    checkout_url: string | null;
    change_state_url: string | null;
    failure_reason: string | null;
};

type Payment = {
    mollie_payment_id: string;
    kind: "setup" | "usage";
    amount: number;
    status: string;
    amount_refunded: number | null;
    amount_charged_back: number | null;
    created_at: string;
    mollie: MolliePayment | null;
    mollie_error: string | null;
};

type Invoice = {
    id: number;
    invoice_number: string;
    kind: "invoice" | "credit_note";
    mollie_payment_id: string;
    issued_at: string;
    total: string;
    status: string;
    has_pdf: boolean;
    emailed_at: string | null;
    email_error: string | null;
    credits_invoice_number: string | null;
};

type HeldWebhook = {
    id: number;
    mollie_payment_id: string;
    received_at: string;
    delivered_at: string | null;
    dropped_at: string | null;
};

type Card = { label: string | null; last_four: string | null; expiry_month: number | null; expiry_year: number | null };

type Preset = { months_ahead: number; as_of: string; period_start: string; period_end: string; dst_change: boolean };

type LabState = {
    sandbox_available: boolean;
    organisation: { id: number; name: string };
    children: { id: number; name: string }[];
    account: {
        billing_status: string;
        past_due_since: string | null;
        grace_ends_at: string | null;
        usage_ceiling: number | null;
        mollie_customer_id: string | null;
        mollie_mandate_id: string | null;
        card: Card | null;
        last_payment_at: string | null;
        last_payment_status: string | null;
        hold_webhooks: boolean;
        mollie_down: boolean;
        join_next_cron: boolean;
    };
    block_reason: string | null;
    meter: { spent: number; ceiling: number | null; remaining: number | null };
    usage_by_month: { month: string; cost_eur: number; billed_dkk: number; rows: number }[];
    periods: Period[];
    payments: Payment[];
    mandates: { id: string; status: string; card_label: string | null; card_number: string | null }[];
    mandates_error: string | null;
    invoices: Invoice[];
    webhooks: HeldWebhook[];
    monthly_presets: Preset[];
    grace_hours: number;
};

type MonthlyResult = {
    months_ahead: number;
    as_of: string;
    period_start: string;
    period_end: string;
    dst_change: boolean;
    period: Period | null;
    charging_period: Period | null;
    charge: (MolliePayment & { mollie_payment_id: string; mollie_error?: string }) | null;
    card: Card | null;
};

type Scenario = { id: string; title: string; steps: string; holds: (s: LabState) => boolean };

const MIN_CHARGE_HINT = "below the minimum charge (10 kr. unless BILLING_MIN_CHARGE_DKK says otherwise)";

const setupPayments = (s: LabState) => s.payments.filter((p) => p.kind === "setup");
const usagePayments = (s: LabState) => s.payments.filter((p) => p.kind === "usage");

const CARD_SCENARIOS: Scenario[] = [
    {
        id: "card",
        title: "Add a test card",
        steps: "Open billing settings, add a card and pay Mollie's test checkout with any test card (choose Paid).",
        holds: (s) => !!s.account.card,
    },
    {
        id: "checkout-failed",
        title: "Card checkout fails",
        steps: "Start a card checkout and choose Failed on Mollie's test page. No card is stored.",
        holds: (s) => setupPayments(s).some((p) => p.status === "failed"),
    },
    {
        id: "checkout-abandoned",
        title: "Card checkout canceled or expired",
        steps: "Start a card checkout and choose Canceled or Expired.",
        holds: (s) => setupPayments(s).some((p) => p.status === "canceled" || p.status === "expired"),
    },
    {
        id: "budget",
        title: "Set a monthly budget",
        steps: "Set a budget in billing settings. Usage is allowed once a card and a budget exist.",
        holds: (s) => s.account.usage_ceiling !== null && s.block_reason === null,
    },
    {
        id: "budget-hit",
        title: "Usage stops at the budget",
        steps: "Inject usage this month (position Now) worth more than the budget. Chat now answers with the budget message.",
        holds: (s) => s.block_reason === "ceiling_reached",
    },
    {
        id: "card-change",
        title: "Replace the card",
        steps: "Change card in billing settings and pay the checkout. The new card is shown.",
        holds: (s) => setupPayments(s).filter((p) => p.status === "paid").length >= 2,
    },
    {
        id: "invoice-mail",
        title: "Invoice reaches your inbox",
        steps: "Settle a charge as Paid. The TEST- invoice PDF is emailed to you (only super admins receive sandbox mail).",
        holds: (s) => s.invoices.some((i) => i.kind === "invoice" && !!i.emailed_at),
    },
    {
        id: "refund",
        title: "Refund issues a credit note",
        steps: "Refund part of a paid charge from the Payments table.",
        holds: (s) => s.invoices.some((i) => i.kind === "credit_note") && s.payments.some((p) => Number(p.amount_refunded) > 0),
    },
    {
        id: "chargeback",
        title: "Chargeback issues a credit note",
        steps: "On Mollie's test page for a paid charge choose Chargeback, then Apply webhook on the payment.",
        holds: (s) => s.payments.some((p) => Number(p.amount_charged_back) > 0),
    },
    {
        id: "revoked",
        title: "Revoked card fails the next charge",
        steps: "Revoke the mandate, inject last month's usage and Bill as of the 1st. The month fails at once and the card is dropped.",
        holds: (s) => s.account.last_payment_status === "mandate_invalid" && !s.account.card,
    },
];

const FIRST_OF_MONTH_SCENARIOS: Scenario[] = [
    {
        id: "bill-paid",
        title: "Last month is charged and paid",
        steps: "Inject last month's usage, Bill as of the 1st of this month, Settle at Mollie as Paid, then Apply webhook or Refresh.",
        holds: (s) => s.periods.some((p) => p.status === "paid") && s.invoices.some((i) => i.invoice_number.startsWith("TEST-")),
    },
    {
        id: "declined",
        title: "A declined charge makes the account past due",
        steps: "Bill as of the 1st, then Settle at Mollie as Failed.",
        holds: (s) => s.periods.some((p) => p.status === "failed") && s.account.billing_status === "past_due",
    },
    {
        id: "grace",
        title: "Usage stops when the grace period ends",
        steps: "While past due, Expire grace. Chat now answers with the past-due message.",
        holds: (s) => s.block_reason === "past_due",
    },
    {
        id: "recover",
        title: "A new card re-charges the failed month",
        steps: "While past due, add a new card. The failed month is charged again at once; settle it as Paid.",
        holds: (s) => s.account.billing_status === "active" && usagePayments(s).length >= 2 && s.periods.some((p) => p.status === "paid"),
    },
    {
        id: "carried",
        title: "A small month is carried into the next",
        steps: `Inject usage ${MIN_CHARGE_HINT} two months back, Bill as of last month's 1st, then inject last month and bill this month. One charge covers both.`,
        holds: (s) => s.periods.some((p) => p.status === "carried" && p.carried_into_id !== null),
    },
    {
        id: "lost-webhook",
        title: "A lost webhook is recovered by the daily job",
        steps: "Hold webhooks, bill, settle as Paid, Drop the held webhook, then Run follow-up with Sync now.",
        holds: (s) => s.webhooks.some((w) => !!w.dropped_at) && s.periods.some((p) => p.status === "paid"),
    },
    {
        id: "outage",
        title: "A Mollie outage leaves the month pending",
        steps: "Switch Mollie down on and bill: the month stays pending. Switch it off and Run follow-up to charge it.",
        holds: (s) => s.account.mollie_down && s.periods.some((p) => p.status === "pending"),
    },
    {
        id: "real-run",
        title: "The real 04:00 run on the 1st picks it up",
        steps: "Switch on Join the next real run. After 04:00 on the 1st the previous month is charged and the switch is off again.",
        holds: (s) => !s.account.join_next_cron && s.periods.some((p) => p.period_start === s.monthly_presets[1]?.period_start),
    },
];

const STATUS_TONE: Record<string, PillTone> = {
    active: "good",
    paid: "good",
    charged: "accent",
    pending: "accent",
    open: "accent",
    past_due: "warn",
    carried: "neutral",
    waived: "neutral",
    none: "neutral",
    failed: "danger",
    canceled: "danger",
    expired: "danger",
};

const tone = (status: string | null | undefined): PillTone => STATUS_TONE[status ?? ""] ?? "neutral";

const kroner = (value: number | string | null | undefined) =>
    value === null || value === undefined
        ? "—"
        : `${Number(value).toLocaleString(appState.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr.`;

const when = (value: string | null | undefined) => (value ? new Date(value).toLocaleString(appState.locale) : "—");

const monthName = (value: string) =>
    new Date(value).toLocaleDateString(appState.locale, { month: "long", year: "numeric", timeZone: "Europe/Copenhagen" });

const cardLabel = (card: Card | null) =>
    card && (card.label || card.last_four)
        ? [card.label, card.last_four ? `•••• ${card.last_four}` : null, card.expiry_month ? `${String(card.expiry_month).padStart(2, "0")}/${String(card.expiry_year).slice(-2)}` : null]
              .filter(Boolean)
              .join(" ")
        : "No card";

const errorText = (error: any): string => {
    const detail = error?.detail ?? error;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((d) => d?.msg ?? JSON.stringify(d)).join("\n");
    return JSON.stringify(detail);
};

const doneKey = (orgId: number) => `billing-lab-done-${orgId}`;

const Button = ({
    children,
    onClick,
    disabled,
    primary,
    danger,
}: {
    children: ReactNode;
    onClick: () => void;
    disabled?: boolean;
    primary?: boolean;
    danger?: boolean;
}) => (
    <button
        type="button"
        className={["btn btn-sm", primary && "btn-primary", danger && "btn-danger"].filter(Boolean).join(" ")}
        disabled={disabled}
        onClick={onClick}
    >
        {children}
    </button>
);

const Switch = ({ on, onChange, disabled }: { on: boolean; onChange: (on: boolean) => void; disabled?: boolean }) => (
    <button
        type="button"
        role="switch"
        aria-checked={on}
        className={`toggle ${on ? "toggle-on" : ""}`}
        disabled={disabled}
        onClick={() => onChange(!on)}
    />
);

const ExternalLink = ({ href, children }: { href: string | null | undefined; children: ReactNode }) =>
    href ? (
        <a className="btn btn-sm" href={href} target="_blank" rel="noreferrer">
            {children}
        </a>
    ) : null;

export const BillingLabPage = observer(() => {
    const navigate = useNavigate();
    const [available, setAvailable] = useState(true);
    const [sandboxes, setSandboxes] = useState<Sandbox[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [state, setState] = useState<LabState | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [lastRun, setLastRun] = useState<MonthlyResult | null>(null);
    const [monthsAhead, setMonthsAhead] = useState(0);
    const [label, setLabel] = useState("");
    const [parentId, setParentId] = useState("");
    const [usage, setUsage] = useState({ amount: "250", unit: "dkk" as "dkk" | "eur", monthOffset: -1, position: "middle" as "now" | "start" | "middle" | "end", orgId: "" });
    const [refundAmount, setRefundAmount] = useState("");
    const [done, setDone] = useState<Record<string, boolean>>({});

    const run = async <T,>(what: string, call: () => Promise<{ data?: T; error?: unknown }>): Promise<T | undefined> => {
        setBusy(what);
        try {
            const { data, error } = await call();
            if (error !== undefined && error !== null) {
                appState.setModal({ title: what, body: errorText(error) });
                return undefined;
            }
            return data;
        } finally {
            setBusy(null);
        }
    };

    const loadList = async () => {
        const data = (await run("Load sandboxes", () => sandboxList())) as
            | { sandbox_available: boolean; sandboxes: Sandbox[] }
            | undefined;
        if (!data) return;
        setAvailable(data.sandbox_available);
        setSandboxes(data.sandboxes);
        if (selectedId === null && data.sandboxes.length > 0) setSelectedId(data.sandboxes[0].id);
    };

    const loadState = async (id = selectedId) => {
        if (id === null) return;
        const data = (await run("Load sandbox", () => sandboxState({ body: { id } }))) as LabState | undefined;
        if (data) setState(data);
    };

    // Every operator action ends by re-reading the sandbox, so the checklist and
    // tables always show what the backend now holds.
    const act = async <T,>(what: string, call: () => Promise<{ data?: T; error?: unknown }>) => {
        const data = await run(what, call);
        await loadState();
        void loadList();
        return data;
    };

    useEffect(() => {
        void loadList();
    }, []);

    useEffect(() => {
        setState(null);
        setLastRun(null);
        if (selectedId === null) return;
        try {
            setDone(JSON.parse(localStorage.getItem(doneKey(selectedId)) ?? "{}"));
        } catch {
            setDone({});
        }
        void loadState(selectedId);
    }, [selectedId]);

    const toggleDone = (id: string) => {
        if (selectedId === null) return;
        const next = { ...done, [id]: !done[id] };
        setDone(next);
        localStorage.setItem(doneKey(selectedId), JSON.stringify(next));
    };

    const create = async () => {
        const parent = parentId.trim() ? Number(parentId) : null;
        const data = (await run("Create sandbox", () =>
            sandboxCreate({ body: { label, parent_organisation_id: parent } }),
        )) as { id: number } | undefined;
        if (!data) return;
        setLabel("");
        setParentId("");
        await loadList();
        setSelectedId(data.id);
        await appState.loadUser();
    };

    const remove = async () => {
        if (selectedId === null || !window.confirm(`Delete ${state?.organisation.name ?? "this sandbox"} and its child organisations?`)) return;
        await run("Delete sandbox", () => sandboxDelete({ body: { id: selectedId } }));
        setSelectedId(null);
        setState(null);
        await loadList();
    };

    const reset = async () => {
        if (selectedId === null || !window.confirm("Reset the sandbox? Its card, budget, usage, charges and TEST- invoices are removed.")) return;
        setLastRun(null);
        await act("Reset sandbox", () => sandboxReset({ body: { id: selectedId } }));
        localStorage.removeItem(doneKey(selectedId));
        setDone({});
    };

    // The monthly job is idempotent, so running the same 1st again only re-reads
    // the month and its charge.
    const billAsOf = async (what: string, months: number) => {
        if (selectedId === null) return;
        const data = (await act(what, () =>
            sandboxRunJob({ body: { id: selectedId, job: "monthly", months_ahead: months } }),
        )) as MonthlyResult | undefined;
        if (data) setLastRun({ ...data, months_ahead: months });
    };

    const applyWebhook = (paymentId: string) =>
        act("Apply webhook", () => sandboxWebhook({ body: { id: selectedId!, action: "apply", payment_id: paymentId } }));

    const setFlag = (flag: "hold_webhooks" | "mollie_down" | "join_next_cron", on: boolean) =>
        act("Update switch", () => sandboxFlags({ body: { id: selectedId!, [flag]: on } }));

    const inject = () =>
        act("Inject usage", () =>
            sandboxInjectUsage({
                body: {
                    id: selectedId!,
                    amount: usage.amount.replace(",", "."),
                    unit: usage.unit,
                    month_offset: usage.monthOffset,
                    position: usage.position,
                    org_id: usage.orgId ? Number(usage.orgId) : null,
                },
            }),
        );

    const refund = (paymentId: string) =>
        act("Refund", () =>
            sandboxRefund({
                body: { id: selectedId!, payment_id: paymentId, amount: refundAmount.trim() ? refundAmount.replace(",", ".") : null },
            }),
        );

    const preset = state?.monthly_presets.find((p) => p.months_ahead === monthsAhead);
    const selected = sandboxes.find((s) => s.id === selectedId);
    const charging = lastRun?.charging_period ?? null;
    const isBusy = busy !== null;

    return (
        <div className="split-layout">
        <div className="split-frame">
            <aside className="split-sidebar gap-3 pr-2">
                <div>
                    <div className="caption mb-1">Billing Lab</div>
                    <p className="detail-text">
                        Sandboxes bill through the Mollie test API: test cards, no real money, TEST- invoices emailed only to super admins.
                    </p>
                </div>
                {!available && (
                    <p className="error-text">MOLLIE_TEST_API_KEY is not set to a Mollie test_ key, so sandboxes cannot reach Mollie.</p>
                )}

                <div className="flex flex-col gap-1">
                    <small className="caption">New sandbox</small>
                    <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label, e.g. november run" />
                    <input
                        className="input"
                        value={parentId}
                        onChange={(e) => setParentId(e.target.value.replace(/\D/g, ""))}
                        placeholder="Parent organisation id (default: yours)"
                    />
                    <Button primary disabled={isBusy || !label.trim() || !available} onClick={create}>
                        Create sandbox
                    </Button>
                </div>

                <div className="flex flex-col gap-1">
                    <small className="caption">Sandboxes</small>
                    {sandboxes.length === 0 && <p className="detail-text">None yet.</p>}
                    {sandboxes.map((s) => (
                        <div
                            key={s.id}
                            className={`nav-item ${s.id === selectedId ? "nav-item-selected" : ""}`}
                            onClick={() => setSelectedId(s.id)}
                        >
                            <div className="flex flex-col min-w-0 flex-grow">
                                <p className="l truncate">{s.name}</p>
                                <p className="detail-text">{s.has_card ? "Card on file" : "No card"}{s.join_next_cron ? " · joins next run" : ""}</p>
                            </div>
                            <Pill tone={tone(s.billing_status)}>{s.billing_status}</Pill>
                        </div>
                    ))}
                </div>
            </aside>

            <main className="split-main overflow-y-auto px-2 pb-4 flex flex-col gap-3">
                {!selected || !state ? (
                    <p className="detail-text">{busy ?? "Create or pick a sandbox."}</p>
                ) : (
                    <>
                        <div className="flex flex-row items-center gap-1 flex-wrap">
                            <h2 className="h2 flex-grow">{state.organisation.name}</h2>
                            {busy && <Pill tone="accent">{busy}…</Pill>}
                            <Button onClick={() => navigate(`/workspace/settings/org/${state.organisation.id}/billing`)}>
                                Open billing settings
                            </Button>
                            <Button disabled={isBusy} onClick={() => loadState()}>
                                Refresh
                            </Button>
                            <Button disabled={isBusy} onClick={reset}>
                                Reset
                            </Button>
                            <Button danger disabled={isBusy} onClick={remove}>
                                Delete
                            </Button>
                        </div>

                        <Section title="Bill as of the 1st" hint="Runs the same job as the 04:00 cron on the 1st, for this sandbox only">
                            <Row>
                                <div className="flex flex-row items-center gap-1 flex-wrap">
                                    <select
                                        className="select w-auto"
                                        value={monthsAhead}
                                        onChange={(e) => setMonthsAhead(Number(e.target.value))}
                                    >
                                        {state.monthly_presets.map((p) => (
                                            <option key={p.months_ahead} value={p.months_ahead}>
                                                {`${when(p.as_of)}: bill ${monthName(p.period_start)}${p.dst_change ? " (DST change)" : ""}`}
                                            </option>
                                        ))}
                                    </select>
                                    <Button primary disabled={isBusy || !available} onClick={() => billAsOf("Bill as of the 1st", monthsAhead)}>
                                        Bill as of the 1st
                                    </Button>
                                </div>
                                {preset && (
                                    <p className="detail-text mt-[.25rem]">
                                        {`Closes ${when(preset.period_start)} to ${when(preset.period_end)} and charges it to the card on file.`}
                                    </p>
                                )}
                            </Row>

                            {lastRun && (
                                <Row
                                    trailing={
                                        <div className="flex flex-col items-end gap-[.35rem]">
                                            <Pill tone={tone(lastRun.charge?.status ?? lastRun.period?.status)}>
                                                {lastRun.charge?.status ?? lastRun.period?.status ?? "no month"}
                                            </Pill>
                                            <div className="flex flex-row gap-[.35rem]">
                                                <ExternalLink href={lastRun.charge?.change_state_url}>Settle at Mollie</ExternalLink>
                                                {lastRun.charge && (
                                                    <Button disabled={isBusy} onClick={() => applyWebhook(lastRun.charge!.mollie_payment_id)}>
                                                        Apply webhook
                                                    </Button>
                                                )}
                                                <Button disabled={isBusy} onClick={() => billAsOf("Refresh charge", lastRun.months_ahead)}>
                                                    Refresh charge
                                                </Button>
                                            </div>
                                        </div>
                                    }
                                >
                                    <p className="l">
                                        {`${monthName(lastRun.period_start)}: ${lastRun.period ? lastRun.period.status : "no month closed"}`}
                                        {lastRun.dst_change ? " · DST change in this month" : ""}
                                    </p>
                                    {charging && charging.total !== null ? (
                                        <p className="detail-text">
                                            {`Net ${kroner(charging.charge_net)} + VAT ${kroner(charging.vat_amount)} = ${kroner(charging.total)}`}
                                            {charging.id !== lastRun.period?.id ? ` (charged with ${monthName(charging.period_start)})` : ""}
                                        </p>
                                    ) : (
                                        <p className="detail-text">{`Usage ${kroner(lastRun.period?.net_amount)}, nothing charged`}</p>
                                    )}
                                    <p className="detail-text">
                                        {`Card: ${cardLabel(lastRun.card)}`}
                                        {lastRun.charge ? ` · ${lastRun.charge.mollie_payment_id}` : ""}
                                        {lastRun.charge?.failure_reason ? ` · ${lastRun.charge.failure_reason}` : ""}
                                        {lastRun.charge?.mollie_error ? ` · ${lastRun.charge.mollie_error}` : ""}
                                    </p>
                                </Row>
                            )}

                            <Row trailing={<Switch on={state.account.join_next_cron} disabled={isBusy} onChange={(on) => setFlag("join_next_cron", on)} />}>
                                <p className="l">Join the next real run</p>
                                <p className="detail-text">
                                    The 04:00 job on the 1st skips sandboxes. With this on, the next run includes this one and switches it off again.
                                </p>
                            </Row>
                        </Section>

                        <Section title="Account">
                            <Row trailing={<Pill tone={tone(state.account.billing_status)}>{state.account.billing_status}</Pill>}>
                                <p className="l">{cardLabel(state.account.card)}</p>
                                <p className="detail-text">
                                    {`Budget ${kroner(state.account.usage_ceiling)} · spent this month ${kroner(state.meter.spent)}`}
                                    {state.account.last_payment_status ? ` · last payment ${state.account.last_payment_status}` : ""}
                                </p>
                                {state.account.billing_status === "past_due" && (
                                    <p className="detail-text">{`Past due since ${when(state.account.past_due_since)}, grace ends ${when(state.account.grace_ends_at)}`}</p>
                                )}
                            </Row>
                            <Row trailing={<Pill tone={state.block_reason ? "danger" : "good"}>{state.block_reason ?? "usage allowed"}</Pill>}>
                                <p className="l">Usage gate</p>
                                <p className="detail-text">What a member of this organisation gets when they start a chat.</p>
                            </Row>
                            <Row>
                                <p className="l">Mandates at Mollie</p>
                                <p className="detail-text">
                                    {state.mandates_error ??
                                        (state.mandates.length
                                            ? state.mandates.map((m) => `${m.id} ${m.status} ${m.card_label ?? ""} ${m.card_number ?? ""}`).join(" · ")
                                            : "None")}
                                </p>
                            </Row>
                        </Section>

                        <Section title="Usage">
                            <Row>
                                <div className="flex flex-row items-end gap-1 flex-wrap">
                                    <input
                                        className="input w-[7rem]"
                                        value={usage.amount}
                                        onChange={(e) => setUsage({ ...usage, amount: e.target.value })}
                                    />
                                    <select className="select w-auto" value={usage.unit} onChange={(e) => setUsage({ ...usage, unit: e.target.value as "dkk" | "eur" })}>
                                        <option value="dkk">kr. billed (excl. VAT)</option>
                                        <option value="eur">EUR LLM cost</option>
                                    </select>
                                    <select
                                        className="select w-auto"
                                        value={usage.monthOffset}
                                        onChange={(e) => setUsage({ ...usage, monthOffset: Number(e.target.value) })}
                                    >
                                        <option value={-2}>Two months ago</option>
                                        <option value={-1}>Last month</option>
                                        <option value={0}>This month</option>
                                        <option value={1}>Next month</option>
                                    </select>
                                    <select
                                        className="select w-auto"
                                        value={usage.position}
                                        onChange={(e) => setUsage({ ...usage, position: e.target.value as typeof usage.position })}
                                    >
                                        <option value="middle">Mid-month</option>
                                        <option value="start">First second</option>
                                        <option value="end">Last second</option>
                                        <option value="now">Now (this month only)</option>
                                    </select>
                                    {state.children.length > 0 && (
                                        <select className="select w-auto" value={usage.orgId} onChange={(e) => setUsage({ ...usage, orgId: e.target.value })}>
                                            <option value="">The sandbox</option>
                                            {state.children.map((c) => (
                                                <option key={c.id} value={c.id}>
                                                    {c.name}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                    <Button disabled={isBusy} onClick={inject}>
                                        Inject usage
                                    </Button>
                                </div>
                            </Row>
                            {state.usage_by_month.length === 0 ? (
                                <EmptyRow>No usage yet.</EmptyRow>
                            ) : (
                                <Row>
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Month</th>
                                                <th>LLM cost</th>
                                                <th>Billed</th>
                                                <th>Rows</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {state.usage_by_month.map((m) => (
                                                <tr key={m.month}>
                                                    <td>{monthName(m.month)}</td>
                                                    <td>{`€${Number(m.cost_eur).toFixed(4)}`}</td>
                                                    <td>{kroner(m.billed_dkk)}</td>
                                                    <td>{m.rows}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Row>
                            )}
                        </Section>

                        <Section title="Mollie's side">
                            <Row trailing={<Switch on={state.account.hold_webhooks} disabled={isBusy} onChange={(on) => setFlag("hold_webhooks", on)} />}>
                                <p className="l">Hold webhooks</p>
                                <p className="detail-text">Webhooks wait in the inbox below until you deliver, repeat or drop them.</p>
                            </Row>
                            <Row trailing={<Switch on={state.account.mollie_down} disabled={isBusy} onChange={(on) => setFlag("mollie_down", on)} />}>
                                <p className="l">Mollie is down</p>
                                <p className="detail-text">Charges fail as in an outage and the month stays pending for the daily job.</p>
                            </Row>
                            <Row>
                                <div className="flex flex-row gap-1 flex-wrap">
                                    <Button disabled={isBusy} onClick={() => act("Run follow-up", () => sandboxRunJob({ body: { id: selectedId!, job: "followup" } }))}>
                                        Run follow-up
                                    </Button>
                                    <Button
                                        disabled={isBusy}
                                        onClick={() => act("Run follow-up", () => sandboxRunJob({ body: { id: selectedId!, job: "followup", sync_now: true } }))}
                                    >
                                        Run follow-up, sync now
                                    </Button>
                                    <Button
                                        disabled={isBusy}
                                        onClick={() => act("Run invoice delivery", () => sandboxRunJob({ body: { id: selectedId!, job: "invoice_delivery" } }))}
                                    >
                                        Run invoice delivery
                                    </Button>
                                    <Button
                                        disabled={isBusy || state.account.billing_status !== "past_due"}
                                        onClick={() => act("Expire grace", () => sandboxExpireGrace({ body: { id: selectedId! } }))}
                                    >
                                        {`Expire grace (${state.grace_hours} h)`}
                                    </Button>
                                    <Button
                                        disabled={isBusy || !state.account.mollie_mandate_id}
                                        onClick={() => act("Revoke mandate", () => sandboxRevokeMandate({ body: { id: selectedId! } }))}
                                    >
                                        Revoke mandate
                                    </Button>
                                </div>
                            </Row>
                            {state.webhooks.length > 0 && (
                                <Row>
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Held webhook</th>
                                                <th>Received</th>
                                                <th>Outcome</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {state.webhooks.map((w) => (
                                                <tr key={w.id} className={w.delivered_at || w.dropped_at ? "data-table-row-inactive" : ""}>
                                                    <td>{w.mollie_payment_id}</td>
                                                    <td>{when(w.received_at)}</td>
                                                    <td>{w.delivered_at ? `delivered ${when(w.delivered_at)}` : w.dropped_at ? `dropped ${when(w.dropped_at)}` : "waiting"}</td>
                                                    <td className="flex flex-row gap-[.25rem]">
                                                        {(["deliver", "deliver_twice", "drop"] as const).map((action) => (
                                                            <Button
                                                                key={action}
                                                                disabled={isBusy}
                                                                onClick={() =>
                                                                    act("Webhook", () => sandboxWebhook({ body: { id: selectedId!, action, inbox_id: w.id } }))
                                                                }
                                                            >
                                                                {action === "deliver" ? "Deliver" : action === "deliver_twice" ? "Deliver twice" : "Drop"}
                                                            </Button>
                                                        ))}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Row>
                            )}
                        </Section>

                        <Section title="Months">
                            {state.periods.length === 0 ? (
                                <EmptyRow>No month closed yet.</EmptyRow>
                            ) : (
                                <Row>
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Month</th>
                                                <th>Status</th>
                                                <th>Usage</th>
                                                <th>Charged</th>
                                                <th>Payment</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {state.periods.map((p) => (
                                                <tr key={p.id}>
                                                    <td>{`${monthName(p.period_start)}${p.dst_change ? " · DST" : ""}`}</td>
                                                    <td>
                                                        <Pill tone={tone(p.status)}>{p.status}</Pill>
                                                    </td>
                                                    <td>{kroner(p.net_amount)}</td>
                                                    <td>{p.total !== null ? kroner(p.total) : p.carried_into_id ? `carried into #${p.carried_into_id}` : "—"}</td>
                                                    <td>{p.mollie_payment_id ?? "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Row>
                            )}
                        </Section>

                        <Section
                            title="Payments"
                            action={
                                <input
                                    className="input w-[10rem]"
                                    value={refundAmount}
                                    onChange={(e) => setRefundAmount(e.target.value)}
                                    placeholder="Refund amount (all)"
                                />
                            }
                        >
                            {state.payments.length === 0 ? (
                                <EmptyRow>No payments yet.</EmptyRow>
                            ) : (
                                <Row>
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Payment</th>
                                                <th>Kind</th>
                                                <th>Amount</th>
                                                <th>Ours</th>
                                                <th>Mollie</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {state.payments.map((p) => (
                                                <tr key={p.mollie_payment_id}>
                                                    <td>{p.mollie_payment_id}</td>
                                                    <td>{p.kind}</td>
                                                    <td>
                                                        {kroner(p.amount)}
                                                        {Number(p.amount_refunded) > 0 ? ` · refunded ${kroner(p.amount_refunded)}` : ""}
                                                        {Number(p.amount_charged_back) > 0 ? ` · charged back ${kroner(p.amount_charged_back)}` : ""}
                                                    </td>
                                                    <td>
                                                        <Pill tone={tone(p.status)}>{p.status}</Pill>
                                                    </td>
                                                    <td>{p.mollie?.status ?? p.mollie_error ?? "—"}</td>
                                                    <td className="flex flex-row gap-[.25rem] flex-wrap">
                                                        {p.mollie?.status === "open" && <ExternalLink href={p.mollie.checkout_url}>Checkout</ExternalLink>}
                                                        <ExternalLink href={p.mollie?.change_state_url}>Settle at Mollie</ExternalLink>
                                                        <Button disabled={isBusy} onClick={() => applyWebhook(p.mollie_payment_id)}>
                                                            Apply webhook
                                                        </Button>
                                                        {p.kind === "usage" && p.mollie?.status === "paid" && (
                                                            <Button disabled={isBusy} onClick={() => refund(p.mollie_payment_id)}>
                                                                Refund
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Row>
                            )}
                        </Section>

                        <Section title="Invoices">
                            {state.invoices.length === 0 ? (
                                <EmptyRow>No invoices yet.</EmptyRow>
                            ) : (
                                <Row>
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Number</th>
                                                <th>Kind</th>
                                                <th>Total</th>
                                                <th>Issued</th>
                                                <th>Email</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {state.invoices.map((i) => (
                                                <tr key={i.id}>
                                                    <td>{i.invoice_number}</td>
                                                    <td>{i.kind === "credit_note" ? `credit note for ${i.credits_invoice_number}` : i.status}</td>
                                                    <td>{kroner(i.total)}</td>
                                                    <td>{when(i.issued_at)}</td>
                                                    <td>{i.emailed_at ? `sent ${when(i.emailed_at)}` : i.email_error ?? "waiting"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Row>
                            )}
                        </Section>

                        {[
                            { title: "Scenarios: card and budget", scenarios: CARD_SCENARIOS },
                            { title: "Scenarios: the 1st of the month", scenarios: FIRST_OF_MONTH_SCENARIOS },
                        ].map((group) => (
                            <Section
                                key={group.title}
                                title={group.title}
                                hint={`${group.scenarios.filter((s) => done[s.id]).length} of ${group.scenarios.length} done`}
                            >
                                {group.scenarios.map((scenario) => {
                                    const holds = scenario.holds(state);
                                    return (
                                        <Row
                                            key={scenario.id}
                                            onClick={() => toggleDone(scenario.id)}
                                            leading={
                                                <input type="checkbox" className="mt-[.2rem]" checked={!!done[scenario.id]} readOnly />
                                            }
                                            trailing={<Pill tone={holds ? "good" : "neutral"}>{holds ? "state matches" : "not yet"}</Pill>}
                                        >
                                            <p className="l">{scenario.title}</p>
                                            <p className="detail-text">{scenario.steps}</p>
                                        </Row>
                                    );
                                })}
                            </Section>
                        ))}
                    </>
                )}
            </main>
        </div>
        </div>
    );
});
